import {
    createContext,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from 'react';
import { tenantConfigPromise, type TenantConfig } from './tenantConfig';

const TenantConfigContext = createContext<TenantConfig | null>(null);

/**
 * Resolves the tenant for the current hostname before rendering children.
 * Auth must not start until this settles: the Auth0 provider needs the
 * tenant's auth0OrgId to scope login (and silent token renewal) to the right
 * Auth0 Organization. Null config = Etcho defaults (unknown host or config
 * endpoint unavailable).
 */
export function TenantConfigProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<{
        loaded: boolean;
        config: TenantConfig | null;
    }>({ loaded: false, config: null });

    useEffect(() => {
        let mounted = true;
        tenantConfigPromise.then(config => {
            if (mounted) setState({ loaded: true, config });
        });
        return () => {
            mounted = false;
        };
    }, []);

    if (!state.loaded) {
        return null;
    }

    return (
        <TenantConfigContext.Provider value={state.config}>
            {children}
        </TenantConfigContext.Provider>
    );
}

export function useTenantConfig(): TenantConfig | null {
    return useContext(TenantConfigContext);
}
